import { useEffect, useState, useCallback } from 'react';
import { Card, Table, Spin, Empty, Select, Button, message, Modal } from 'antd';
import axios from 'axios';
import { GET_TAXATION_URL, GET_TAXSTATUSCHOICES_URL, BASE_URL } from '../../../utils/URLS';

const { Column } = Table;
const { Option } = Select;

export default function TaxationViewer({ company_id }) {
  const [taxData, setTaxData] = useState([]);
  const [taxStatusOptions, setTaxStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatedStatuses, setUpdatedStatuses] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);


  const fetchTaxationData = useCallback(async () => {
    if (!company_id) return;

    setLoading(true);
    try {
      const { data } = await axios.get(`${GET_TAXATION_URL}?company_id=${company_id}`, {
        headers: {
          Authorization: `Token ${localStorage.getItem("token")}`,
        },
      });

      setTaxData(data || []);

      // Fetch history for each taxation record
      const allHistories = {};
      await Promise.all(
        (data || []).map(async (taxation) => {
          try {
            const res = await axios.get(`${BASE_URL}taxhistory/list-by-taxation/?taxation=${taxation.id}`, {
              headers: {
                Authorization: `Token ${localStorage.getItem("token")}`,
              },
            });
            allHistories[taxation.id] = res.data;
          } catch (err) {
            console.error(`Failed to fetch history for taxation ID ${taxation.id}`, err);
            allHistories[taxation.id] = [];
          }
        })
      );

      setHistoryData(allHistories);
    } catch (err) {
      console.error("Failed to fetch taxation data:", err);
    } finally {
      setLoading(false);
    }
  }, [company_id]);

  // Fetch available tax status transitions
  const fetchTaxStatusChoices = useCallback(async (taxId) => {
    try {
      const { data } = await axios.get(`${GET_TAXSTATUSCHOICES_URL}?tax_id=${taxId}`, {
        headers: {
          Authorization: `Token ${localStorage.getItem("token")}`,
        },
      });
      setTaxStatusOptions(data || []);
    } catch (err) {
      console.error("Error fetching tax status choices:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchTaxationData();
  }, [fetchTaxationData]);

  // Fetch status choices after taxation data is loaded
  useEffect(() => {
    if (taxData.length > 0) {
      fetchTaxStatusChoices(taxData[0].id);
    }
  }, [taxData, fetchTaxStatusChoices]);

  const handleStatusChange = (id, value) => {
    setUpdatedStatuses((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSave = async () => {
    const updateCalls = Object.entries(updatedStatuses).map(async ([id, statusDisplay]) => {
      const selectedOption = taxStatusOptions.find(opt => opt.target_label === statusDisplay);
      if (!selectedOption) return;

      try {
        await axios.put(`${GET_TAXATION_URL}${id}/`, {
          tax_status: selectedOption.target,
        }, {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });
      } catch (error) {
        console.error(`Failed to update tax status for ID ${id}:`, error);
      }
    });

    try {
      await Promise.all(updateCalls);
      message.success("All changes saved successfully!");
      setUpdatedStatuses({});
      fetchTaxationData(); // 🔄 Refresh updated data
    } catch {
      message.error("Failed to save some changes.");
    }
  };

  return (
    <>
    <Card
      title={<span className="text-xl font-semibold">Taxation Details</span>}
      style={{ width: "100%" }}
      extra={
        <Button
  onClick={handleSave}
  disabled={Object.keys(updatedStatuses).length === 0}
  className={`transition-all font-semibold px-4 py-1.5 rounded-md
    ${Object.keys(updatedStatuses).length === 0
      ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
      : 'bg-blue-600 hover:bg-blue-700 text-white'}
  `}
>
  Save Changes
</Button>

      }
    >
      {loading ? (
        <Spin tip="Loading taxation data..." />
      ) : taxData.length === 0 ? (
        <Empty description="No data found." />
      ) : (
        <Table
          dataSource={taxData}
          rowKey={(record) => record.id}
          pagination={false}
          scroll={{ x: true }}
        >
          {Object.keys(taxData[0])
            .filter(key => key !== 'tax_status' && key !== 'company' && key !== 'company_name' && key !== 'tax_status_display')
            .map((key) => (
              <Column
                key={key}
                title={key.replace(/_/g, " ").toUpperCase()}
                dataIndex={key}
              />
            ))}

          <Column
            title="TAX STATUS"
            key="tax_status_display"
            render={(_, record) => (
              <Select
                defaultValue={record.tax_status_display}
                style={{ width: 180 }}
                onChange={(value) => handleStatusChange(record.id, value)}
              >
                {taxStatusOptions.map((option) => (
                  <Option key={option.target} value={option.target_label} />
                ))}
              </Select>
            )}
          />
        </Table>
      )}
    </Card>
    <Card
  title={<span className="text-xl font-semibold">🕘 Taxation History</span>}
  style={{ width: "100%", marginTop: 24 }}
  bodyStyle={{ padding: '24px' }}
>
  {loading ? (
    <Spin tip="Loading history..." />
  ) : Object.keys(historyData).length === 0 ? (
    <Empty description="No taxation history found." imageStyle={{ height: 60 }} />
  ) : (
    Object.entries(historyData).map(([taxationId, historyList]) => (
      <div
        key={taxationId}
        style={{
          marginBottom: 32,
          background: '#f9f9f9',
          borderRadius: 12,
          padding: '16px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}
      >
        
        <Table
          dataSource={historyList}
          rowKey={(record, index) => `${taxationId}-${index}`}
          pagination={false}
          scroll={{ x: true }}
        >
          <Column
            title="📌 Status"
            dataIndex="status"
            key="status"
            render={(text) => (
              <span style={{ color: text === "Completed" ? 'green' : '#555' }}>{text || 'N/A'}</span>
            )}
          />
          <Column
            title="📅 Date"
            dataIndex="created_date"
            key="created_date"
            render={(date) => new Date(date).toLocaleString()}
          />
          <Column
            title=" 👤 Created By"
            dataIndex="created_by_name"
            key="created_by_name"
            render={(name) => name || "Unknown"}
          />
          <Column
            title="🧠 Task"
            key="task"
            render={(_, record) =>
              record.Task ? (
                <Button
                  size="small"
                  type="primary"
                  ghost
                  onClick={() => {
                    setSelectedTask(record.Task);
                    setIsTaskModalVisible(true);
                  }}
                >
                  View Task
                </Button>
              ) : (
                <span style={{ color: '#888' }}><i>No Task Linked</i></span>
              )
            }
          />
        </Table>
      </div>
    ))
  )}
</Card>


    <Modal
  title={<span className="text-lg font-semibold">📝 Task Details</span>}
  open={isTaskModalVisible}
  onCancel={() => setIsTaskModalVisible(false)}
  footer={[
    <Button
      key="close"
      onClick={() => setIsTaskModalVisible(false)}
      className="rounded-md px-4 py-1.5 bg-gray-100 hover:bg-gray-200"
    >
      Close
    </Button>
  ]}
  width={600}
  bodyStyle={{ padding: '24px' }}
>
  {selectedTask ? (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-500 uppercase font-semibold">Title</p>
        <p className="text-base text-gray-800">{selectedTask.title || '—'}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500 uppercase font-semibold">Description</p>
        <p className="text-base text-gray-800">{selectedTask.description || '—'}</p>
      </div>
      <div>
  <p className="text-sm text-gray-500 uppercase font-semibold">Assigned To</p>
  <div className="text-base text-gray-800">
    {selectedTask.assigned_to_name || '—'}
    {Array.isArray(selectedTask.assigned_to_email) ? (
      <div className="flex flex-wrap gap-2 mt-1">
        {selectedTask.assigned_to_email.map((email, idx) => (
          <span
            key={idx}
            className="bg-gray-100 text-sm text-gray-700 px-2 py-1 rounded-md"
          >
            {email}
          </span>
        ))}
      </div>
    ) : selectedTask.assigned_to_email ? (
      <span className="block text-sm text-gray-500 mt-1">
        {selectedTask.assigned_to_email}
      </span>
    ) : (
      <span className="block text-sm text-gray-400 mt-1">No email</span>
    )}
  </div>
</div>
      <div>
        <p className="text-sm text-gray-500 uppercase font-semibold">First Name</p>
        <p className="text-base text-gray-800">{selectedTask.created_by_first_name || '—'}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500 uppercase font-semibold">Last Name</p>
        <p className="text-base text-gray-800">{selectedTask.created_by_last_name || '—'}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500 uppercase font-semibold">Email Address</p>
        <p className="text-base text-gray-800">{selectedTask.created_by_email || '—'}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500 uppercase font-semibold">Due Date</p>
        <p className="text-base text-gray-800">
          {selectedTask.due_date ? new Date(selectedTask.due_date).toLocaleString() : '—'}
        </p>
      </div>
      <div>
        <p className="text-sm text-gray-500 uppercase font-semibold">Status</p>
        <p className="text-base text-gray-800">{selectedTask.status || '—'}</p>
      </div>
    </div>
  ) : (
    <Empty description="No task details found." />
  )}
  
</Modal>


    </>
  );
}
